import {
  fetchHighIntentOffers, fetchOfferBasedRecommendations,
  fetchPeopleLikeMeRecommendations, fetchPersonalisedAlternativeOffers, fetchTopDeals,
} from 'actions/RecommendationActions'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Pane from 'components/Common/Pane/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import Heading from 'components/Luxkit/Typography/Heading'
import HighIntentRecommendationTileLoader from 'components/OfferList/HighIntent/HighIntentRecommendationTileLoader'
import RecentlyViewedTile from 'components/OfferList/HighIntent/RecentlyViewedTile'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { ANYWHERE_PLACE_ID } from 'constants/search'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fillArray, take } from 'lib/array/arrayUtils'
import { getQueryParams } from 'lib/search/searchUtils'
import { rem } from 'polished'
import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { getHighIntentHotelOfferIds, getHighIntentHotelOffers } from 'selectors/recomendations/highIntentSelectors'
import { getPersonalisedAlternativeOfferIds } from 'selectors/recomendations/personalisedAlternatives'
import styled from 'styled-components'
import TopPicksForMeBanner from './TopPicksForMeBanner'
import TopPicksForMeCategoryTilesCarousel, { ImageTileItem } from './TopPicksForMeCategoryTilesCarousel'
import TopPicksForMeFrontPageOfferCarousel from './TopPicksForMeFrontPageOfferCarousel'
import TopPicksForMeOfferCarouselSection from './TopPicksForMeOfferCarouselSection'
import TopPicksForMeHighIntentOffers from './TopPicksForMeRecentlyViewedOffers'
import { getGraphRecommendations } from 'selectors/recomendations/graphSelectors'
import { getRecommendationUserId } from 'selectors/accountSelectors'
import { accountAccessShowLogin } from 'actions/UiActions'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'

const TopSectionContainer = styled(Pane)`
  padding-bottom: ${rem(24)};
  padding-top: ${rem(24)};
`

interface Props {
  personalisedPeopleLikeMe: App.RecommendationState['personalisedPeopleLikeMe'];
  topDeals: App.RecommendationState['topDeals'];
  search: string;
  modalOpen: boolean;
  region: string
}

const holdayTypeCategories = [
  { label: 'All Inclusive', holidayTypes: ['All-inclusive'], image: { id: 'padqe2e6gp2cmiitz5ak' } },
  { label: 'Boutique', holidayTypes: ['Boutique'], image: { id: 'zev3an6zeuootm4bkozv' } },
  { label: 'City Break', holidayTypes: ['City Break', 'City break'], image: { id: 'tjdgfmunbkuwvm6qb1' } },
  { label: 'Sun & Beach', holidayTypes: ['Sun & Beach'], image: { id: 'byd5q1h0ntru3pj88rq' } },
  { label: 'Outdoor & Adventure', holidayTypes: ['Outdoor & Adventure'], image: { id: 'subrmlsidv9aldihxlcc' } },
  { label: 'Wellness & Relaxation', holidayTypes: ['Spa Break', 'Spa break', 'Wellness', 'Relaxed'], image: { id: 'ma7wvecs4pm46zi2yr94' } },
]

const popularPlaces = [
  { label: 'Fiji', placeId: 'le_72b32a1f754ba1c09b3695e0cb6cde7f', image: { url: 'https://images.ctfassets.net/wddhvj9ktohl/7gZfL7JtYmRXcYAWR1dUXh/247efe9f28536257d486f26cd6179ad2/Set01_Fiji.jpg?w=512' } },
  { label: 'Australia', placeId: 'le_d3d9446802a44259755d38e6d163e820', image: { url: 'https://images.ctfassets.net/wddhvj9ktohl/43sa4GserBbW29yOcBl8qT/3137e06b520c83ba6a594a7e133f7b9c/Set01_Australia.jpg?w=512' } },
  { label: 'Thailand', placeId: 'le_8f85517967795eeef66c225f7883bdcb', image: { url: 'https://images.ctfassets.net/wddhvj9ktohl/6YGXYsG3bAM5yu3Gr8FGs3/cd2e7674401d28f42ab8814799f9a7e4/Set01_Thailand.jpg?w=512' } },
  { label: 'Bali', placeId: 'le_64930b7151b6bc3e289f37d5da62ac9f', image: { url: 'https://images.ctfassets.net/wddhvj9ktohl/1DgjzSuEobj7pKTRJDTAcT/d6e4dc87a82670d8be868c528bdb6b53/Set01_Bali.jpg?w=512' } },
  { label: 'Maldives', placeId: 'le_2723d092b63885e0d7c260cc007e8b9d', image: { url: 'https://images.ctfassets.net/wddhvj9ktohl/9ksoWtBV2fPhT69LarSXH/a7c49e2f7aeb6c705d93f07e3c52ca32/Set01_Maldives.jpg?w=512' } },
  { label: 'USA', placeId: 'le_757b505cfd34c64c85ca5b5690ee5293', image: { url: 'https://images.ctfassets.net/wddhvj9ktohl/6XFxQSXniFOkwxD0cHPhM/5c51319a22f174d7b2b80ca9ef5aac3c/Set01_USA.jpg?w=512' } },
]

const TRACKING_PAGE_SOURCE = 'Top Picks for Me'

function TopPicksForMePage({
  personalisedPeopleLikeMe,
  topDeals,
  search,
  modalOpen,
  region,
}: Props) {
  const dispatch = useAppDispatch()
  const userId = useAppSelector(getRecommendationUserId)

  useEffect(() => {
    dispatch(fetchPeopleLikeMeRecommendations(userId))
    dispatch(fetchTopDeals(region))
    dispatch(fetchHighIntentOffers(userId))
    dispatch(fetchPersonalisedAlternativeOffers(region, userId))
    if (!userId) {
      dispatch(accountAccessShowLogin())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, region])

  const highIntentState = useAppSelector(state => state.recommendations.highIntent.state)
  const highIntentOffers = useAppSelector(getHighIntentHotelOffers)
  const highIntentOfferIds = useAppSelector(getHighIntentHotelOfferIds)
  const highIntentLoading = !!userId && highIntentState === 'loading'
  const hasHighIntent = highIntentState === 'done' && highIntentOfferIds.length > 0
  const canShowHighIntent = highIntentLoading || hasHighIntent

  const alternatives = useAppSelector(state => state.recommendations.personalisedAlternatives)
  const alternativeOfferIds = useAppSelector(getPersonalisedAlternativeOfferIds)
  const alternativesLoading = !!userId && alternatives.state === 'loading'
  const canShowAlternatives = alternativesLoading || alternatives.offers.length > 0

  // for graph recos
  // first offer in HI or top deal is used to seed for graph reco
  const topOfferId = useMemo(() => {
    if (highIntentState === 'done' || (!userId && topDeals.state === 'done')) {
      return highIntentOfferIds[0] ?? topDeals.offerIds[0] ?? ''
    }
    return ''
  }, [userId, highIntentOfferIds, highIntentState, topDeals.offerIds, topDeals.state])

  useEffect(() => {
    // only use graph reco if no alternaitives available
    if (!!topOfferId && !canShowAlternatives) {
      dispatch(fetchOfferBasedRecommendations(topOfferId, false))
    }
  }, [dispatch, topOfferId, canShowAlternatives])

  const offersSimilarToTopOffer = useAppSelector<App.OfferBasedGraphRecommendation>(state => getGraphRecommendations(state, topOfferId))
  const graphLereVersion = useAppSelector(state => state.recommendations.lereVersion)
  const graphRecommendationsLoading = !canShowAlternatives && canShowHighIntent && offersSimilarToTopOffer.state === 'loading'
  const canShowGraphRecommendations = graphRecommendationsLoading || offersSimilarToTopOffer.offerIds.length > 0

  const similarOfferIds = alternativeOfferIds.length ? alternativeOfferIds : offersSimilarToTopOffer.offerIds
  const similarOffersLereVersion = canShowAlternatives ? alternatives.lereVersion : graphLereVersion
  const canShowSimilarOffers = canShowAlternatives || canShowGraphRecommendations
  const similarOffersLoading = alternativesLoading || graphRecommendationsLoading

  const categoryTileItems = useMemo<Array<ImageTileItem>>(() => {
    const buildHref = (holidayTypes: Array<string>) => `/search?${getQueryParams(search, ANYWHERE_PLACE_ID, 'Anywhere', { holidayTypes })}`

    return holdayTypeCategories.map((tile => ({
      ...tile,
      href: buildHref(tile.holidayTypes),
    })))
  }, [search])

  const popularTileItems = useMemo<Array<ImageTileItem>>(() => {
    const buildHref = (place: string, placeId: string) => `/search?${getQueryParams(search, placeId, place)}`

    return popularPlaces.map((place) => (
      { ...place, href: buildHref(place.label, place.placeId) }
    ))
  }, [search])

  const peopleLikeMeOfferIds = useMemo(() =>
    personalisedPeopleLikeMe.offers.map(offer => offer.offerId),
  [personalisedPeopleLikeMe])

  const excludedFrontPageOfferIds = useMemo(() => {
    return [
      ...highIntentOfferIds,
      ...similarOfferIds,
      ...peopleLikeMeOfferIds,
      ...topDeals.offerIds,
    ]
  }, [
    highIntentOfferIds,
    similarOfferIds,
    peopleLikeMeOfferIds,
    topDeals.offerIds,
  ])

  // HI offers are deduped from top deals
  const topDealsExcluded = useMemo(() => {
    // first 3 top deals are used to back fill HI if HI not available
    if (!hasHighIntent) {
      return take(topDeals.offerIds, 3)
    }
    return highIntentOfferIds
  }, [hasHighIntent, highIntentOfferIds, topDeals.offerIds])

  if (!userId && modalOpen) {
    // While login modal is open show loading indicator behind
    return <LoadingIndicator />
  } else if (!userId) {
    // this page doesn't make sense without a user, it's personalisation *for* a user, can't not have one!
    return <Error404PageWithOffers />
  }

  return (<Pane>
    <TopPicksForMeBanner />
    <TopSectionContainer>
      {highIntentLoading && <LayoutContainer>
        <VerticalSpacer gap={20}>
          <Heading variant="heading5">
            Finding your personalised top picks...
          </Heading>
          <LoadingBox style={{ height: 108, maxWidth: 108 }} />
        </VerticalSpacer>
      </LayoutContainer>}

      {/* High Intent carousel (Recently Viewed / Hot Lead) */}
      {hasHighIntent && <TopPicksForMeHighIntentOffers
        highIntentOffers={highIntentOffers}
      />}
      {/* HI carousel using top deals as fallback */}
      {!canShowHighIntent && <LayoutContainer>
        <VerticalSpacer gap={20}>
          <Heading variant="heading5">
            You might like these offers
          </Heading>
          <CardCarousel pageSize={3}>
            {fillArray(3, index => topDeals.offerIds[index]).map((id, index) => <React.Fragment key={id ?? index}>
              {!id && <HighIntentRecommendationTileLoader />}
              {id && <RecentlyViewedTile
                key={id}
                offerId={id}
                position={index + 1}
              />}
            </React.Fragment>)}
          </CardCarousel>
        </VerticalSpacer>
      </LayoutContainer>}
    </TopSectionContainer>
    {/* Similar offers (either alternatives or graph seeded from high intent) */}
    {canShowSimilarOffers && <TopPicksForMeOfferCarouselSection
      heading={similarOffersLoading ? 'Looking for similar offers' : 'Other similar offers'}
      subheading="Based on what you recently viewed"
      offerIds={similarOfferIds}
      fetching={similarOffersLoading}
      excludedOfferIds={highIntentOfferIds}
      lereVersion={similarOffersLereVersion}
      trackingPageSource={TRACKING_PAGE_SOURCE}
      trackingUiTouchpoint="Similar Recommendations Carousel V1.0.0"
    />}
    {/* top deals recos (rendered at top as a fallback for similar offers) */}
    {!canShowSimilarOffers && <TopPicksForMeOfferCarouselSection
      heading="This week’s best selling offers"
      offerIds={topDeals.offerIds}
      fetching={topDeals.state === 'loading' || highIntentLoading}
      excludedOfferIds={topDealsExcluded}
      lereVersion={topDeals.lereVersion}
      trackingPageSource={TRACKING_PAGE_SOURCE}
      trackingUiTouchpoint="Top Deals Recommendations Carousel V1.0.0"
    />}
    {/* people like me recos */}
    <TopPicksForMeOfferCarouselSection
      heading="Popular offers for you"
      subheading="Based on what you’ve booked and viewed in the past"
      offerIds={peopleLikeMeOfferIds}
      fetching={personalisedPeopleLikeMe.state === 'loading' || highIntentLoading}
      excludedOfferIds={highIntentOfferIds}
      lereVersion={personalisedPeopleLikeMe.lereVersion}
      trackingPageSource={TRACKING_PAGE_SOURCE}
      trackingUiTouchpoint="People Like Me Recommendations Carousel V1.0.0"
    />
    {/* pupolar destination recos */}
    <TopPicksForMeCategoryTilesCarousel
      heading="Explore popular destinations"
      items={popularTileItems}
    />
    {/* top deals recos (at lower place when we show similar offers at top) */}
    {canShowSimilarOffers && <TopPicksForMeOfferCarouselSection
      heading="This week’s best selling offers"
      offerIds={topDeals.offerIds}
      fetching={topDeals.state === 'loading' || similarOffersLoading}
      excludedOfferIds={topDealsExcluded}
      lereVersion={topDeals.lereVersion}
      trackingPageSource={TRACKING_PAGE_SOURCE}
      trackingUiTouchpoint="Top Deals Recommendations Carousel V1.0.0"
    />}
    {/* home page recos */}
    <TopPicksForMeFrontPageOfferCarousel
      fetching={
        topDeals.state === 'loading' ||
        highIntentLoading ||
        personalisedPeopleLikeMe.state === 'loading' ||
        (canShowSimilarOffers && similarOffersLoading)
      }
      excludedOfferIds={excludedFrontPageOfferIds}
      trackingPageSource={TRACKING_PAGE_SOURCE}
      trackingUiTouchpoint="Homepage Recommendations Carousel V1.0.0"
    />
    {/* holiday types recos */}
    <TopPicksForMeCategoryTilesCarousel
      heading="Stays we think you’ll love"
      items={categoryTileItems}
    />
  </Pane>)
}

function mapState(state: App.State) {
  return {
    personalisedPeopleLikeMe: state.recommendations.personalisedPeopleLikeMe,
    topDeals: state.recommendations.topDeals,
    search: state.router.location.search,
    modalOpen: (state.ui.modal.modalType === 'accountAccess' && state.ui.modal.open),
    region: state.geo.currentRegionCode,
  }
}

export default connect(mapState)(TopPicksForMePage)
